import { useState } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { PlanningPeriodDetailDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useOneCreateActionState } from 'modules/core/lib/oneCreateActions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import SubmissionPeriodFormModal from 'modules/planning/components/SubmissionPeriodFormModal'
import ManageCollaboratorsModal from 'modules/planning/components/ManageCollaborators'
import { PeriodWizard } from 'modules/planning/components/PeriodWizard'
import type { PeriodRoleContext } from 'modules/planning/roles'

import { useOne } from 'modules/ai/lib/useOne'

const PeriodDetailPage = () => {
  const { periodId } = useParams<{ periodId: string }>()
  const { data: oneCreationData } = useOneCreateActionState<Record<string, unknown>>()
  const cameFromCreate = !!oneCreationData
  const { company } = useCurrentSession()
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false)
  const [isCollaboratorsModalOpen, setIsCollaboratorsModalOpen] = useState(false)
  const [isEditWizardOpen, setIsEditWizardOpen] = useState(false)

  const { data, loading, error } = useQuery(PlanningPeriodDetailDocument, {
    variables: { id: periodId ?? '' },
    skip: !periodId,
  })

  const period = data?.planning?.period
  const periodRoleData = (period?.periodRole ?? [])[0]
  const roleContext: PeriodRoleContext | undefined = periodRoleData
    ? { higherRole: periodRoleData.higherRole, roles: periodRoleData.roles }
    : undefined
  const planningScopedRole = roleContext?.roles?.find((role) => (role.planningIds?.length ?? 0) > 0)
  const editorPlanningRole = roleContext?.roles?.find(
    (role) => role.role === 'editor' && (role.planningIds?.length ?? 0) > 0
  )
  const planningId = planningScopedRole?.planningIds?.[0] ?? undefined
  // Each planning period supports at most one active submission period; take the first one.
  const submissionPeriod = period?.submissionPeriods?.[0]
  const isEditorForPlanning = !!editorPlanningRole
  const activePeriod = period?.status === 'active'
  const canCreateRequest =
    (period?._policies?.canCreateRequest?.can ?? false) || (!!isEditorForPlanning && activePeriod)

  const handleActionClick = (key: string) => {
    if (key === 'edit-period') {
      setIsEditWizardOpen(true)
    }

    if (key === 'manage-collaborators') {
      setIsCollaboratorsModalOpen(true)
    }

    if (key === 'submission-period') {
      setIsSubmissionModalOpen(true)
    }
  }

  const { isThinking } = useOne<Record<string, unknown> | null>(
    ['headcountPlanning'],
    period ? { period } : null,
    {
      autoOpenChat: cameFromCreate,
    }
  )

  return (
    <ResponsivePage.Body
      variant="full"
      onActionClick={handleActionClick}
      loading={loading}
      error={error}
      authorized={!!period}
    >
      <Outlet context={{ isThinking, period, periodId, roleContext, canCreateRequest }} />

      {isSubmissionModalOpen && periodId && (
        <SubmissionPeriodFormModal
          periodId={periodId}
          companyId={company.id}
          submissionPeriod={
            submissionPeriod
              ? {
                  id: submissionPeriod.id,
                  startDate: submissionPeriod.startDate,
                  endDate: submissionPeriod.endDate,
                }
              : undefined
          }
          onClose={() => setIsSubmissionModalOpen(false)}
        />
      )}

      {isCollaboratorsModalOpen && periodId && (
        <ManageCollaboratorsModal
          periodId={periodId}
          planningId={planningId}
          onClose={() => setIsCollaboratorsModalOpen(false)}
        />
      )}

      {isEditWizardOpen && period && (
        <PeriodWizard
          period={period}
          onClose={() => setIsEditWizardOpen(false)}
          onSuccess={() => setIsEditWizardOpen(false)}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PeriodDetailPage
